import type { ComponentType } from "react";
import { CheckCircle2, Circle, CircleDot, CircleSlash, Loader2, XCircle } from "lucide-react";
import { cn } from "~/lib/utils";
import { PlanTaskStatus, type PlanTask } from "./derivePlan";

interface PlanChecklistProps {
  tasks: PlanTask[];
  // Whether the agent is currently working. Only then does the in-progress
  // step's icon spin; on an idle or finished turn it shows a static "current
  // step" marker, so a persisted plan doesn't read as a perpetual spinner.
  isActive?: boolean;
}

interface StatusStyle {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  iconClassName: string;
  labelClassName: string;
}

// Mirrors the tool-call status idiom in `ToolCallItem` (Loader2 spin /
// CheckCircle2 emerald / XCircle) so the plan reads as part of the same system.
const STATUS_STYLES: Record<PlanTaskStatus, StatusStyle> = {
  [PlanTaskStatus.pending]: {
    icon: Circle,
    iconClassName: "text-muted-foreground",
    labelClassName: "text-foreground",
  },
  [PlanTaskStatus.in_progress]: {
    icon: Loader2,
    iconClassName: "text-foreground",
    labelClassName: "text-foreground font-medium",
  },
  [PlanTaskStatus.completed]: {
    icon: CheckCircle2,
    iconClassName: "text-emerald-600",
    labelClassName: "text-muted-foreground line-through",
  },
  [PlanTaskStatus.cancelled]: {
    icon: XCircle,
    iconClassName: "text-muted-foreground",
    labelClassName: "text-muted-foreground line-through",
  },
  [PlanTaskStatus.blocked]: {
    icon: CircleSlash,
    iconClassName: "text-muted-foreground",
    labelClassName: "text-muted-foreground",
  },
};

// Left-pad per nesting depth. Data-prep nests one level (files under the ingest
// step); a deeper plan clamps to the last stop rather than run off the edge.
const DEPTH_INDENT = ["", "pl-5", "pl-10", "pl-14"] as const;

interface RenderRow {
  task: PlanTask;
  depth: number;
}

// Orders the flat task list for rendering: each root followed by its children,
// depth-first, so `add_subtask` steps sit under their parent regardless of the
// order the fold produced. A task whose parent is absent renders as a root; the
// `seen` guard breaks any parent cycle, and unreached tasks still render flat.
function orderForRender(tasks: PlanTask[]): RenderRow[] {
  const ids = new Set(tasks.map((task) => task.id));
  const childrenByParent = new Map<string, PlanTask[]>();
  const roots: PlanTask[] = [];
  for (const task of tasks) {
    const parent = task.parentId;
    if (parent !== undefined && ids.has(parent)) {
      const siblings = childrenByParent.get(parent) ?? [];
      siblings.push(task);
      childrenByParent.set(parent, siblings);
    } else {
      roots.push(task);
    }
  }
  const rows: RenderRow[] = [];
  const seen = new Set<string>();
  const walk = (task: PlanTask, depth: number) => {
    if (seen.has(task.id)) return;
    seen.add(task.id);
    rows.push({ task, depth });
    for (const child of childrenByParent.get(task.id) ?? []) walk(child, depth + 1);
  };
  for (const root of roots) walk(root, 0);
  for (const task of tasks) if (!seen.has(task.id)) rows.push({ task, depth: 0 });
  return rows;
}

// The live task plan for a turn, rendered as a checklist. The plan is folded
// from the turn's `Planning`-capability tool calls by `derivePlan`; this
// component is a pure view over the result. Renders nothing for an empty plan.
export function PlanChecklist({ tasks, isActive = false }: PlanChecklistProps) {
  if (tasks.length === 0) return null;

  const completed = tasks.filter((task) => task.status === PlanTaskStatus.completed).length;
  const rows = orderForRender(tasks);

  return (
    <section
      aria-label="Task plan"
      className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm"
    >
      <div className="flex items-baseline justify-start px-4 pt-3 pb-2">
        <span className="text-xs font-normal text-muted-foreground">
          {completed}/{tasks.length}
        </span>
      </div>
      <ul className="flex flex-col gap-1.5 px-4 pb-3">
        {rows.map(({ task, depth }) => (
          <PlanRow key={task.id} task={task} depth={depth} isActive={isActive} />
        ))}
      </ul>
    </section>
  );
}

function PlanRow({ task, depth, isActive }: { task: PlanTask; depth: number; isActive: boolean }) {
  const style = STATUS_STYLES[task.status] ?? STATUS_STYLES[PlanTaskStatus.pending];
  const isInProgress = task.status === PlanTaskStatus.in_progress;
  // The in-progress step spins only while the agent is working; idle, it shows a
  // static "current step" dot so a finished turn's plan doesn't spin forever.
  const spin = isInProgress && isActive;
  const Icon = isInProgress && !isActive ? CircleDot : style.icon;
  // Surface the present-continuous label only while the step is running — it
  // reads as an activity ("Profiling the upload") rather than a step title.
  const label = isInProgress && task.activeForm ? task.activeForm : task.content;
  const indent = DEPTH_INDENT[Math.min(depth, DEPTH_INDENT.length - 1)];

  return (
    <li className={cn("flex items-start gap-2 text-sm", indent)}>
      <Icon
        className={cn("mt-[3px] size-3.5 shrink-0", style.iconClassName, spin && "animate-spin")}
        aria-hidden
      />
      <span className={cn("min-w-0 break-words", style.labelClassName)}>{label}</span>
    </li>
  );
}
